/**
 * How much of a voice sample is speech (ADR-0062), counted while it records from the one thing
 * the recorder tells of the sound: the microphone's level, a few times a second. A voice is on
 * once the level stands clear of the room's noise and off once it falls back to it — two
 * thresholds, so a level between them changes nothing. The room's noise is tracked as the level's
 * slow-rising minimum: it follows a quieter room at once and a louder one slowly. A breath inside
 * a sentence is counted with the sentence; a pause is not.
 *
 * It hears loudness, not words, and tells a voice from a noise by one thing only: a voice comes
 * back down to the room at every breath and between words, a fan or an engine does not. A level
 * that has stood clear of the room for a few seconds without once coming back is therefore the
 * room, grown louder: it stops counting, and the floor is let up to it. An engine that starts
 * mid-sample counts as a voice for those few seconds and no longer; a noise that rises and falls
 * as speech does still counts. The count is the operator's guide to when to stop — the server
 * measures the audio itself before it makes a voiceprint. The numbers below were chosen for the
 * recorder's meter scale (`level-meter.ts`). Quiet speech is judged relative to the room's
 * noise, with an absolute floor of −60 dBFS; real microphones still need field validation.
 *
 * Where the meter could not be opened every level reads zero and no speech could ever be
 * counted: after a few seconds of that, the recorded time is counted instead. Where the room was
 * listened to before the count (`room-check.ts`), the meter starts from that room. Pure.
 */
import type { VoiceCounting } from '@/components/humint/voice/voice-types'
import { meterLevel } from '../recording/level-meter'

/** Maximum on-margin on the visual scale; quieter rooms use a relative margin instead. */
export const SPEECH_ON_MARGIN = 0.12

/** Maximum off-margin, below the on-margin to avoid flickering between words. */
export const SPEECH_OFF_MARGIN = 0.06

/** Float samples preserve quiet speech: −60 dBFS can count when clear of the room. */
export const SPEECH_MIN_ON_LEVEL = meterLevel(10 ** (-60 / 20))

/** The absolute off-threshold is 6 dB below the on-threshold. */
export const SPEECH_MIN_OFF_LEVEL = meterLevel(10 ** (-66 / 20))

/** The meter takes √RMS: +6 dB to turn on, +3 dB to stay on, converted to that scale. */
const SPEECH_ON_RATIO = 10 ** (6 / 40)
const SPEECH_OFF_RATIO = 10 ** (3 / 40)

/** A voice must hold this long before it counts — a slammed door is no speech; then it counts from its start. */
export const SPEECH_ATTACK_MS = 150

/**
 * A gap inside speech no longer than this — a breath, the stop of a consonant — is counted with
 * the speech once the voice is back. A longer one is a pause: the voice is off and it counts for
 * nothing.
 */
export const SPEECH_HANG_MS = 400

/** The floor follows a quieter room this fast (a time constant)… */
export const FLOOR_FALL_MS = 400

/** …settles on steady noise that is no voice this fast… */
export const FLOOR_SETTLE_MS = 1_500

/** …and rises under a voice this slowly, so half a minute of speech does not become the floor. */
export const FLOOR_RISE_MS = 20_000

/**
 * A level that has not come back under the off-threshold for longer than this is no voice: it is
 * the room, grown louder. Nothing of it counts from there on, and the floor settles on it.
 */
export const SPEECH_STEADY_MS = 4_000

/**
 * One level proves no more than this much of that: a meter that reads only now and then (a
 * throttled tab) has not seen the breaths between its readings, and takes longer to call a level
 * steady.
 */
export const STEADY_SAMPLE_MS = 250

/**
 * The floor is never tracked above this: a room that loud lets no voice stand clear of it — the
 * on-threshold over it is the top of the scale.
 */
export const FLOOR_CEILING = 1 - SPEECH_ON_MARGIN

/** No level at all this far into a recording: the meter is not working, and time is counted. */
export const NO_LEVEL_GRACE_MS = 3_000

/** One level stands for at most this long: a sample that comes late (a throttled tab) counts no more. */
export const MAX_SAMPLE_MS = 1_000

/** The meter between two levels. */
export interface SpeechMeter {
  /** The time of every level taken. */
  elapsedMs: number
  /** The speech counted from the level. */
  voicedMs: number
  /** The room's noise as tracked; null before the first level above zero. */
  floor: number | null
  /** A voice is on: also through a gap not yet longer than the hang time. */
  speaking: boolean
  /** While no voice is on: for how long the level has stood over the on-threshold. */
  risingMs: number
  /** While a voice is on: for how long the level has been under the off-threshold. */
  gapMs: number
  /** For how long no voice has been heard; 0 while one is on. */
  silentMs: number
  /**
   * For how long the level has been seen not to come back under the off-threshold, as a voice
   * does at every breath: `STEADY_SAMPLE_MS` at most for each level.
   */
  heldMs: number
  /** A level above zero has arrived: the meter works. */
  heard: boolean
}

/** The meter before the first level. */
export const SILENT_METER: SpeechMeter = {
  elapsedMs: 0,
  voicedMs: 0,
  floor: null,
  speaking: false,
  risingMs: 0,
  gapMs: 0,
  silentMs: 0,
  heldMs: 0,
  heard: false,
}

/**
 * The meter as it starts after the room was listened to (`room-check.ts`): its floor is the room
 * the check measured, so the first word is judged against the room and not taken for it. `floor`
 * null: the check heard nothing — the meter starts as it does without one.
 */
export function meterInRoom(floor: number | null): SpeechMeter {
  if (floor === null || !(floor > 0)) return SILENT_METER
  return { ...SILENT_METER, floor: Math.min(floor, FLOOR_CEILING), heard: true }
}

/** One level of the microphone and the time since the one before it. */
export interface LevelSample {
  /** 0..1 on the recorder's meter scale. */
  level: number
  dtMs: number
}

/** The two thresholds over a floor. */
export function speechThresholds(floor: number): { on: number; off: number } {
  return {
    on: Math.max(SPEECH_MIN_ON_LEVEL, Math.min(floor * SPEECH_ON_RATIO, floor + SPEECH_ON_MARGIN)),
    off: Math.max(
      SPEECH_MIN_OFF_LEVEL,
      Math.min(floor * SPEECH_OFF_RATIO, floor + SPEECH_OFF_MARGIN)
    ),
  }
}

/**
 * The floor after a level: fast down, slow up — and slowest under what may be a voice. It starts
 * at the first level above zero: a meter still waking up reads nothing, which is no room.
 */
function trackFloor(floor: number | null, sample: LevelSample, voiced: boolean): number | null {
  if (floor === null) return sample.level > 0 ? Math.min(sample.level, FLOOR_CEILING) : null
  const rise = voiced ? FLOOR_RISE_MS : FLOOR_SETTLE_MS
  const constant = sample.level < floor ? FLOOR_FALL_MS : rise
  const next = floor + (sample.level - floor) * (1 - Math.exp(-sample.dtMs / constant))
  return Math.min(next, FLOOR_CEILING)
}

type Count = Pick<SpeechMeter, 'voicedMs' | 'speaking' | 'risingMs' | 'gapMs' | 'silentMs'>

/** No voice is on: one that holds for the attack time turns on, counted from its start. */
function whileSilent(meter: SpeechMeter, loud: boolean, ms: number): Count {
  const risingMs = loud ? meter.risingMs + ms : 0
  if (risingMs >= SPEECH_ATTACK_MS) {
    const voicedMs = meter.voicedMs + risingMs
    return { voicedMs, speaking: true, risingMs: 0, gapMs: 0, silentMs: 0 }
  }
  const { voicedMs, silentMs } = meter
  return { voicedMs, speaking: false, risingMs, gapMs: 0, silentMs: silentMs + ms }
}

/** A voice is on: it counts, a short gap with it once it is back, and a long one ends it. */
function whileSpeaking(meter: SpeechMeter, quiet: boolean, ms: number): Count {
  if (!quiet) {
    const voicedMs = meter.voicedMs + meter.gapMs + ms
    return { voicedMs, speaking: true, risingMs: 0, gapMs: 0, silentMs: 0 }
  }
  const gapMs = meter.gapMs + ms
  const { voicedMs } = meter
  if (gapMs > SPEECH_HANG_MS) {
    return { voicedMs, speaking: false, risingMs: 0, gapMs: 0, silentMs: gapMs }
  }
  return { voicedMs, speaking: true, risingMs: 0, gapMs, silentMs: 0 }
}

/** The meter after one more level. */
export function advanceSpeechMeter(meter: SpeechMeter, sample: LevelSample): SpeechMeter {
  if (sample.dtMs <= 0) return meter
  const level = Math.min(1, Math.max(0, sample.level))
  // Judged against the room as it was before this level, not one this level has already moved.
  const { on, off } = speechThresholds(meter.floor ?? Math.min(level, FLOOR_CEILING))
  const counted = Math.min(sample.dtMs, MAX_SAMPLE_MS)
  const heldMs = level < off ? 0 : meter.heldMs + Math.min(counted, STEADY_SAMPLE_MS)
  // No breath for this long: it is the room, whatever its level — neither loud nor a voice.
  const steady = heldMs > SPEECH_STEADY_MS
  const count = meter.speaking
    ? whileSpeaking(meter, steady || level < off, counted)
    : whileSilent(meter, !steady && level >= on, counted)
  const voiced = !steady && (meter.speaking || level >= on)
  return {
    ...count,
    heldMs,
    elapsedMs: meter.elapsedMs + sample.dtMs,
    floor: trackFloor(meter.floor, { level, dtMs: sample.dtMs }, voiced),
    heard: meter.heard || level > 0,
  }
}

/** Whether speech is counted, or time: no level at all for the first seconds of a recording. */
export function speechCounting(meter: SpeechMeter): VoiceCounting {
  return !meter.heard && meter.elapsedMs >= NO_LEVEL_GRACE_MS ? 'time' : 'speech'
}

/** What the ring shows of the meter. */
export interface SpeechReading {
  /** The speech counted: the recorded time where no level arrives. */
  speechMs: number
  speaking: boolean
  silentMs: number
  counting: VoiceCounting
}

export function readSpeechMeter(meter: SpeechMeter): SpeechReading {
  if (speechCounting(meter) === 'time') {
    return { speechMs: meter.elapsedMs, speaking: true, silentMs: 0, counting: 'time' }
  }
  const { voicedMs, speaking, silentMs } = meter
  return { speechMs: voicedMs, speaking, silentMs, counting: 'speech' }
}
